'use client';

import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Flex,
  Grid,
  GridItem,
  Icon,
  Input,
  Portal,
  RadioCard,
  Text,
} from '@chakra-ui/react';
import Image from 'next/image';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useCallback, useContext, useEffect, useState } from 'react';
import { CartContext } from '@/contexts/CartContext';
import { ProductProps } from '@/types/Product';
import FormInputNumber from '@/components/Form/form-input-num';
import { MdAccessTime, MdOutlineShoppingCart } from 'react-icons/md';
import ContactLensesOptions from './ContactLenses';
import { FaInfo } from 'react-icons/fa6';
import { usePromotions } from '@/contexts/PromotionsContext';
import { getMinimumValueHint, getProductBasePrice, getProductDiscount } from '@/utils/getProductDiscount';
import { Tooltip } from '@/components/ui/tooltip';
import WishlistButton from '@/components/WishlistButton';
import WaitlistForm from '@/components/WaitlistForm';
import ComingSoon from '@/components/ProductPage/ComingSoon';
import DeliveryOptions from '@/components/ProductPage/DeliveryOptions';
import LensTags from '@/components/ProductPage/LensTags';
import { isRayBanBrand, RayBanSeal } from '@/components/RayBanCertified';
import { isPublished, LEGAL_DOCUMENTS } from '@/config/legalDocuments';
import {
  pickDefaultColorSize,
  pickDefaultVariation,
} from '@/utils/defaultProductOption';

// Definindo o schema de validação com Zod
const schema = z.object({
  product: z
    .string({
      error: 'Producto obrigatório',
    })
    .min(1, { message: 'Producto obrigatório' }),
  product_options: z
    .string({
      error: 'Opção Producto obrigatório',
    })
    .min(1, { message: 'Opção Producto obrigatório' }),
  quantity: z
    .number({ error: 'Quantidade mínima é 1' })
    .min(1, 'Quantidade mínima é 1')
    .max(100, 'Quantidade máxima é 100'),
  product_options_label: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

const ProductOptions = ({
  product,
  handleOptionChange,
}: {
  product: ProductProps;
  handleOptionChange: (optionId: string) => void;
}) => {
  const [addView, setAddView] = useState(false);
  const [priceProduct, setPriceProduct] = useState(0);
  const [priceProductTotal, setPriceProductTotal] = useState(0);
  const [lowestPrice30d, setLowestPrice30d] = useState<number | null>(null);
  const [limitStock, setLimitStock] = useState(0);
  const [productOptions, setProductOptions] = useState<string>();
  const [numEyes, setNumEyes] = useState(1);
  const [open, setOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState<null | {
    name: string;
    color: string;
  }>(null);
  // Só as lentes de contacto usam este estado: o formulário de prescrição é
  // longo, por isso começa escondido atrás de um botão "Configurar lentes"
  // até o cliente querer preenchê-lo. Os outros tipos não têm nada para
  // colapsar (Simples não tem seletor; Variação/Cor+Tamanho já mostram tudo
  // sempre visível).
  const [openInfo, setOpenInfo] = useState(
    product.product_type !== 'CONTACT_LENSES',
  );

  const { addItem } = useContext(CartContext);
  const { promotions } = usePromotions();
  const discountInfo = getProductDiscount(product, promotions);
  const discountMinValueHint = discountInfo
    ? getMinimumValueHint(discountInfo.rule)
    : null;

  const {
    control,
    handleSubmit,
    register,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const watchedProductOptions = watch('product_options');
  const watchedProductOptionsLabel = watch('product_options_label');

  const wishlistOptId =
    product.product_type === 'SIMPLE' ? null : watchedProductOptions || null;
  // Ao contrário da wishlist, a lista de espera de stock não tem granularidade
  // por prescrição nas lentes de contacto (o stock é do produto todo) — por
  // isso CONTACT_LENSES também usa product_opt_id null, tal como SIMPLE.
  const waitlistOptId =
    product.product_type === 'SIMPLE' ||
    product.product_type === 'CONTACT_LENSES'
      ? null
      : watchedProductOptions || null;
  const wishlistOptLabel =
    product.product_type === 'SIMPLE'
      ? null
      : watchedProductOptionsLabel || null;

  // Foto representativa de uma cor (a marcada como default, senão a
  // primeira associada a essa cor) — usada no seletor de cor em vez de uma
  // simples bolinha. Produtos sem nenhuma foto própria por cor caem para a
  // bolinha de cor como recurso alternativo.
  const getColorImage = useCallback(
    (colorId: string): string | null => {
      const colorImages = product.images.filter(
        (image) => image.color_id === colorId,
      );
      return (
        colorImages.find((image) => image.default)?.path ??
        colorImages[0]?.path ??
        null
      );
    },
    [product.images],
  );

  const onSubmit = useCallback(
    (data: FormData) => {
      setOpenInfo(false);
      addItem(
        data.product,
        data.quantity,
        product.type_opt,
        data.product_options,
        data.product_options_label,
      );
    },
    [addItem, product],
  );

  useEffect(() => {
    if (product.product_type === 'SIMPLE' && product.simple) {
      setAddView(true);
      setPriceProduct(product.simple.price_tax);
      setPriceProductTotal(product.simple.price_tax);
      setLimitStock(product.simple.stock);
      setLowestPrice30d(product.simple.lowest_price_tax_30d ?? null);
    } else if (product.product_type === 'VARIATION' && product.variations) {
      // A primeira variação COM stock vem pré-selecionada — preço e botão de
      // comprar ficam prontos de imediato, sem exigir um clique extra.
      const firstVariation = pickDefaultVariation(product.variations);
      if (firstVariation) {
        setPriceProduct(firstVariation.price_tax);
        setPriceProductTotal(firstVariation.price_tax);
        setLimitStock(firstVariation.stock);
        setLowestPrice30d(firstVariation.lowest_price_tax_30d ?? null);
        setValue('product_options', firstVariation.id);
        setValue('product_options_label', firstVariation.name);
        setAddView(true);
        handleOptionChange(firstVariation.id);
      } else {
        setAddView(false);
      }
    } else if (product.product_type === 'COLOR_SIZE' && product.colors) {
      // A primeira cor/tamanho COM stock vem pré-selecionada — mesma lógica
      // da variação, aplicada às duas dimensões (cor + tamanho). É a mesma
      // opção cujo preço vai para os dados estruturados (Google).
      const defaultOption = pickDefaultColorSize(product.colors);
      if (defaultOption) {
        const { color: firstColor, size: firstSize } = defaultOption;
        const value = `${firstColor.id},${firstSize.id}`;

        setSelectedColor({
          name: firstColor.color_name,
          color: firstColor.color_codes,
        });
        setPriceProduct(firstSize.price_tax);
        setPriceProductTotal(firstSize.price_tax);
        setLimitStock(firstSize.stock);
        setLowestPrice30d(firstSize.lowest_price_tax_30d ?? null);
        setValue('product_options', value);
        setProductOptions(value);
        setValue(
          'product_options_label',
          `${firstColor.color_name} | ${firstSize.size}`,
        );
        setAddView(true);
        handleOptionChange(firstColor.id);
      } else {
        setAddView(false);
      }
    } else if (product.product_type === 'CONTACT_LENSES' && product.contact_lenses) {
      setAddView(false);
      setPriceProduct(product.contact_lenses.price_tax);
      setPriceProductTotal(product.contact_lenses.price_tax);
      setLimitStock(product.contact_lenses.stock);
      setLowestPrice30d(product.contact_lenses.lowest_price_tax_30d ?? null);
    } else {
      setAddView(false);
    }
  }, [product, setValue, handleOptionChange]);

  // Produto ainda por lançar: sem opções, sem preço, sem carrinho.
  if (product.coming_soon) return <ComingSoon product={product} />;

  // Selo "Loja certificada Ray-Ban" à direita do nome, só nesta marca.
  const isRayBan = isRayBanBrand(product.brand?.name);

  const brandModelBadges = (
    <Flex w="100%" gap="6px" flexWrap="wrap" mt="6px">
      <Flex
        px="10px"
        py="4px"
        borderRadius="full"
        bg={{ base: 'rgba(0,176,202,0.06)', _dark: 'rgba(0,176,202,0.1)' }}
        border="1px solid rgba(0,176,202,0.2)"
      >
        <Text fontSize="12px" fontWeight="600" color="#00B0CA">
          {product.brand.name}
        </Text>
      </Flex>
      {product.model &&
        product.model
          .split(';')
          .map((modelPart) => modelPart.trim())
          .filter((modelPart) => modelPart.length > 0)
          .map((modelPart) => (
            <Flex
              key={modelPart}
              px="10px"
              py="4px"
              borderRadius="full"
              bg={{ base: 'rgba(0,0,0,0.04)', _dark: 'rgba(255,255,255,0.06)' }}
              border="1px solid"
              borderColor={{ base: 'rgba(0,0,0,0.08)', _dark: 'rgba(255,255,255,0.1)' }}
            >
              <Text
                fontSize="12px"
                fontWeight="600"
                color={{ base: 'rgba(0,0,0,0.45)', _dark: 'rgba(255,255,255,0.45)' }}
              >
                {modelPart}
              </Text>
            </Flex>
          ))}
    </Flex>
  );

  const noticesContent = (
    <>
      {product.is_pre_sale && (
        <Flex
          align="center"
          gap="8px"
          mt="10px"
          py="10px"
          px="12px"
          borderRadius="12px"
          bg={{ base: 'rgba(124,58,237,0.08)', _dark: 'rgba(124,58,237,0.14)' }}
          border="1px solid rgba(124,58,237,0.25)"
        >
          <Text
            fontSize="13px"
            fontWeight="700"
            color="rgba(124,58,237,0.9)"
          >
            Pré-venda
          </Text>
        </Flex>
      )}
      {product.waiting_time && (
        <Flex
          align="center"
          gap="8px"
          mt="10px"
          py="10px"
          px="12px"
          borderRadius="12px"
          bg={{ base: 'rgba(255,159,10,0.08)', _dark: 'rgba(255,159,10,0.12)' }}
          border="1px solid rgba(255,159,10,0.2)"
        >
          <Icon fontSize="16px" color="rgba(180,110,10,0.9)" flexShrink={0}>
            <MdAccessTime />
          </Icon>
          <Text
            fontSize="13px"
            fontWeight="500"
            color={{ base: 'rgba(0,0,0,0.68)', _dark: 'rgba(255,255,255,0.75)' }}
          >
            {product.waiting_time}
          </Text>
        </Flex>
      )}
    </>
  );

  // Fora do selectorsContent de propósito: esse bloco é reaproveitado duas
  // vezes no layout "dividido" (mobile + desktop) — um Dialog lá dentro
  // montaria duas instâncias a partilhar o mesmo estado `open`, e por usar
  // Portal, as duas tentariam abrir por cima uma da outra.
  const colorPreviewDialog = product.product_type === 'COLOR_SIZE' && (
    <Dialog.Root open={open} onOpenChange={(e) => setOpen(e.open)} size="xs">
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            bg={{ base: '#FFFFFF', _dark: '#0A1B2B' }}
            borderRadius="20px"
            border="2px solid"
            borderColor={{ base: 'rgba(0,0,0,0.07)', _dark: 'rgba(255,255,255,0.06)' }}
            boxShadow="0 8px 40px rgba(0,0,0,0.18)"
            overflow="hidden"
            pos="relative"
            p="0"
          >
            {/* Color strip */}
            <Flex
              w="100%"
              h="140px"
              flexShrink={0}
              style={{
                background: selectedColor?.color
                  ? `linear-gradient(135deg, ${selectedColor.color})`
                  : 'rgba(0,0,0,0.06)',
              }}
            />
            {/* Info row */}
            <Flex flexDir="column" gap="3px" px="20px" py="16px">
              <Text
                fontSize="16px"
                fontWeight="700"
                color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}
              >
                {selectedColor?.name}
              </Text>
              <Text
                fontSize="11px"
                fontWeight="600"
                letterSpacing="0.5px"
                textTransform="uppercase"
                color={{ base: 'rgba(0,0,0,0.4)', _dark: 'rgba(255,255,255,0.35)' }}
              >
                Cor disponível
              </Text>
            </Flex>
            {/* Floating close */}
            <Dialog.CloseTrigger asChild>
              <CloseButton
                size="sm"
                pos="absolute"
                top="10px"
                right="10px"
                borderRadius="full"
                bg="rgba(0,0,0,0.22)"
                color="white"
                border="1.5px solid rgba(255,255,255,0.28)"
                transition="all 0.2s"
                _hover={{ bg: 'rgba(0,0,0,0.38)' }}
              />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );

  const selectorsContent = (
    <Flex w="100%" flexDir="column" gap="10px" p="10px">
      {/* Nas lentes de contacto, os badges já vêm junto do título (ramo
          próprio mais abaixo) — não se repetem aqui dentro do painel. */}
      {product.product_type !== 'CONTACT_LENSES' && brandModelBadges}

      {product.product_type === 'SIMPLE' && product.simple && (
        <Input
          type="hidden"
          {...register('product_options')}
          value={'Sem variaçao'}
        />
      )}

      {product.type_opt === 2 &&
        product.variations &&
        product.variations.length > 0 && (
          <Controller
            control={control}
            name="product_options"
            defaultValue=""
            render={({ field }) => (
              <RadioCard.Root
                variant="outline"
                colorPalette="cyan"
                value={field.value}
                onChange={(e: React.ChangeEvent<HTMLDivElement>) => {
                  const selectedID = (e.target as HTMLInputElement).value;
                  field.onChange(selectedID);
                  product.variations?.forEach((variation) => {
                    if (variation.id === selectedID) {
                      setPriceProduct(variation.price_tax);
                      setPriceProductTotal(variation.price_tax);
                      setLimitStock(variation.stock);
                      setLowestPrice30d(
                        variation.lowest_price_tax_30d ?? null,
                      );
                      handleOptionChange(selectedID);
                      setAddView(true);
                      setValue(
                        'product_options_label',
                        `${variation.name}`,
                      );
                    }
                  });
                }}
              >
                <Grid
                  w="100%"
                  templateColumns="repeat(auto-fit, minmax(150px, 1fr))"
                  gap="10px"
                  justifyItems="center"
                >
                  {product.variations?.map((variation) => (
                    <GridItem width="150px" key={variation.id}>
                      <RadioCard.Item
                        key={variation.id}
                        value={variation.id}
                      >
                        <RadioCard.ItemHiddenInput />
                        <RadioCard.ItemControl>
                          <RadioCard.ItemContent>
                            {/* Sem preço aqui: é sempre o price_tax em
                                catálogo, nunca reflete uma campanha ativa —
                                o preço correto (com desconto, se houver)
                                só aparece no total, depois de escolhida a
                                opção. */}
                            <RadioCard.ItemText>
                              {variation.name}
                            </RadioCard.ItemText>
                          </RadioCard.ItemContent>
                          <RadioCard.ItemIndicator />
                        </RadioCard.ItemControl>
                      </RadioCard.Item>
                    </GridItem>
                  ))}
                </Grid>
              </RadioCard.Root>
            )}
          />
        )}

      {product.product_type === 'COLOR_SIZE' &&
        product.colors &&
        product.colors.length > 0 && (
          <Controller
            control={control}
            name="product_options"
            defaultValue=""
            render={({ field }) => (
              <Flex w="100%" flexDir="column" gap="16px">
                  <Flex flexDir="column" gap="8px">
                    <Text
                      fontSize="11px"
                      fontWeight="600"
                      letterSpacing="0.5px"
                      textTransform="uppercase"
                      color={{ base: 'rgba(0,0,0,0.4)', _dark: 'rgba(255,255,255,0.35)' }}
                    >
                      Cor
                    </Text>
                    <Flex flexWrap="wrap" gap="10px">
                      {product.colors?.map((color) => {
                        const isSelected =
                          field.value.split(',')[0] === color.id ||
                          productOptions?.split(',')[0] === color.id;
                        const colorImage = getColorImage(color.id);
                        return (
                          <Flex
                            key={color.id}
                            as="button"
                            flexDir="column"
                            align="center"
                            gap="6px"
                            w="64px"
                            cursor="pointer"
                            onClick={() => {
                              setSelectedColor({
                                name: color.color_name,
                                color: color.color_codes,
                              });
                              handleOptionChange(color.id);
                              setPriceProduct(color.sizes[0].price_tax);
                              setPriceProductTotal(color.sizes[0].price_tax);
                              setLimitStock(color.sizes[0].stock);
                              setLowestPrice30d(
                                color.sizes[0].lowest_price_tax_30d ?? null,
                              );

                              if (color.sizes.length === 1) {
                                // Único tamanho disponível — seleciona-o
                                // logo, sem obrigar o cliente a escolher.
                                const onlySize = color.sizes[0];
                                const value = `${color.id},${onlySize.id}`;
                                field.onChange(value);
                                setProductOptions(value);
                                setValue(
                                  'product_options_label',
                                  `${color.color_name} | ${onlySize.size}`,
                                );
                                setAddView(true);
                              } else {
                                field.onChange(`${color.id}`);
                                setProductOptions(`${color.id}`);
                                setAddView(false);
                              }
                            }}
                          >
                            <Flex
                              pos="relative"
                              w="56px"
                              h="56px"
                              borderRadius="14px"
                              border="2px solid"
                              borderColor={
                                isSelected
                                  ? '#00B0CA'
                                  : { base: 'rgba(0,0,0,0.1)', _dark: 'rgba(255,255,255,0.12)' }
                              }
                              bg={{ base: '#F8FAFB', _dark: '#0C1B2A' }}
                              align="center"
                              justify="center"
                              overflow="hidden"
                              boxShadow={
                                isSelected ? '0 0 0 3px rgba(0,176,202,0.18)' : 'none'
                              }
                              transition="all 0.15s"
                              _hover={{ borderColor: '#00B0CA' }}
                            >
                              {colorImage ? (
                                <Image
                                  src={colorImage}
                                  alt={color.color_name}
                                  fill
                                  sizes="56px"
                                  style={{ objectFit: 'contain', padding: '6px' }}
                                />
                              ) : (
                                <Box
                                  w="22px"
                                  h="22px"
                                  borderRadius="full"
                                  flexShrink={0}
                                  border="1.5px solid"
                                  borderColor={{ base: 'rgba(0,0,0,0.12)', _dark: 'rgba(255,255,255,0.15)' }}
                                  style={{
                                    background: color.color_codes.includes(',')
                                      ? `linear-gradient(to right, ${color.color_codes})`
                                      : color.color_codes,
                                  }}
                                />
                              )}
                              <Flex
                                pos="absolute"
                                top="2px"
                                right="2px"
                                w="16px"
                                h="16px"
                                borderRadius="full"
                                bg="rgba(0,0,0,0.45)"
                                align="center"
                                justify="center"
                                cursor="pointer"
                                transition="background 0.15s"
                                _hover={{ bg: 'rgba(0,176,202,0.9)' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedColor({
                                    name: color.color_name,
                                    color: color.color_codes,
                                  });
                                  setOpen(true);
                                }}
                              >
                                <Icon w="7px" h="7px" color="white">
                                  <FaInfo />
                                </Icon>
                              </Flex>
                            </Flex>
                            <Text
                              fontSize="11px"
                              fontWeight={isSelected ? '600' : '500'}
                              color={
                                isSelected
                                  ? '#00B0CA'
                                  : { base: '#0A1B2B', _dark: '#E2EFF5' }
                              }
                              textAlign="center"
                              lineHeight="1.25"
                              style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {color.color_name}
                            </Text>
                          </Flex>
                        );
                      })}
                    </Flex>
                  </Flex>
                  {(() => {
                    const selectedColorData =
                      product.colors?.find(
                        (color) =>
                          (field.value &&
                            field.value.split(',')[0] === color.id) ||
                          productOptions === color.id,
                      ) ||
                      product.colors?.find(
                        (color) => productOptions === color.id,
                      );
                    return (
                      selectedColorData &&
                      selectedColorData.sizes &&
                      selectedColorData.sizes.length > 0 && (
                        <Flex flexDir="column" gap="8px">
                          <Text
                            fontSize="11px"
                            fontWeight="600"
                            letterSpacing="0.5px"
                            textTransform="uppercase"
                            color={{ base: 'rgba(0,0,0,0.4)', _dark: 'rgba(255,255,255,0.35)' }}
                          >
                            Tamanho
                          </Text>
                          <Flex flexWrap="wrap" gap="8px">
                            {selectedColorData.sizes.map((size) => {
                              const isSizeSelected =
                                field.value.split(',')[1] === size.id;
                              return (
                                <Flex
                                  key={size.id}
                                  as="button"
                                  px="14px"
                                  py="7px"
                                  borderRadius="full"
                                  border="2px solid"
                                  borderColor={
                                    isSizeSelected
                                      ? '#00B0CA'
                                      : { base: 'rgba(0,0,0,0.08)', _dark: 'rgba(255,255,255,0.1)' }
                                  }
                                  bg={
                                    isSizeSelected
                                      ? { base: 'rgba(0,176,202,0.06)', _dark: 'rgba(0,176,202,0.08)' }
                                      : { base: 'transparent', _dark: 'transparent' }
                                  }
                                  cursor="pointer"
                                  transition="all 0.15s"
                                  _hover={{ borderColor: '#00B0CA' }}
                                  onClick={() => {
                                    setPriceProduct(size.price_tax);
                                    setPriceProductTotal(size.price_tax);
                                    setLimitStock(size.stock);
                                    setLowestPrice30d(
                                      size.lowest_price_tax_30d ?? null,
                                    );
                                    setAddView(true);
                                    field.onChange(
                                      `${selectedColorData.id},${size.id}`,
                                    );
                                    setProductOptions(
                                      `${selectedColorData.id},${size.id}`,
                                    );
                                    setValue(
                                      'product_options_label',
                                      `${selectedColorData.color_name} | ${size.size}`,
                                    );
                                  }}
                                >
                                  <Text
                                    fontSize="13px"
                                    fontWeight={isSizeSelected ? '600' : '500'}
                                    color={
                                      isSizeSelected
                                        ? '#00B0CA'
                                        : { base: '#0A1B2B', _dark: '#E2EFF5' }
                                    }
                                  >
                                    {size.size}
                                  </Text>
                                </Flex>
                              );
                            })}
                          </Flex>
                        </Flex>
                      )
                    );
                  })()}
                  {/* Tags da lente da cor selecionada — marcadas por cor no
                      backoffice, por isso mudam ao trocar de cor. */}
                  <LensTags
                    tags={
                      product.colors?.find(
                        (color) =>
                          color.id ===
                          (field.value || productOptions || '').split(',')[0],
                      )?.lens_tags
                    }
                  />
                </Flex>
              )}
            />
        )}

      {product.product_type === 'CONTACT_LENSES' && product.contact_lenses && (
        <Flex flexDir="column" gap="10px" w="100%">
          {/* Só no telemóvel: no computador o formulário já vem sempre
              aberto (hidden acima de lg), não há nada para alternar. */}
          <Button
            type="button"
            onClick={() => setOpenInfo((prevState) => !prevState)}
            display={{ base: 'flex', lg: 'none' }}
            alignSelf={openInfo ? 'flex-start' : 'stretch'}
            justifyContent="center"
            alignItems="center"
            w={openInfo ? 'auto' : '100%'}
            px={openInfo ? '14px' : '20px'}
            h={openInfo ? '34px' : '46px'}
            minH={openInfo ? '34px' : '46px'}
            bg={openInfo ? 'rgba(0,176,202,0.08)' : '#00B0CA'}
            color={openInfo ? '#00B0CA' : '#fff'}
            borderRadius="full"
            border={openInfo ? '1px solid rgba(0,176,202,0.2)' : 'none'}
            fontWeight="700"
            fontSize={openInfo ? '12px' : '15px'}
            boxShadow={openInfo ? 'none' : '0 4px 20px rgba(0,176,202,0.35)'}
            transition="all 0.2s"
            _hover={{ bg: openInfo ? 'rgba(0,176,202,0.15)' : '#009AB5' }}
          >
            {openInfo ? 'Fechar' : 'Configurar lentes'}
          </Button>
          <Box
            w="100%"
            display="block"
            maxH={{ base: openInfo ? '900px' : '0px', lg: 'none' }}
            overflow="hidden"
            transition="max-height 0.4s ease"
          >
            <ContactLensesOptions
              contact={product.contact_lenses || null}
              stageDefault={1}
              onFormComplete={(data: string) => {
                if (product.contact_lenses) {
                  if (!addView) setAddView(true);
                  setProductOptions(data);
                  setValue('product_options', data);
                  if (
                    JSON.parse(data).OD &&
                    JSON.parse(data).OE &&
                    numEyes !== 2
                  ) {
                    setPriceProductTotal((pv) => pv * 2);
                    setPriceProduct((pv) => pv * 2);
                    setLowestPrice30d((pv) => (pv ? pv * 2 : pv));
                    setNumEyes(2);
                  }
                }
              }}
              onReset={() => {
                setAddView(false);
                if (product.contact_lenses) {
                  setPriceProduct(product.contact_lenses.price_tax);
                  setPriceProductTotal(product.contact_lenses.price_tax);
                  setLowestPrice30d(
                    product.contact_lenses.lowest_price_tax_30d ?? null,
                  );
                }
                setNumEyes(1);
                setValue('product_options', '');
              }}
            />
          </Box>
        </Flex>
      )}

      {noticesContent}
    </Flex>
  );

  const ctaContent = (
    <Flex
      alignSelf="end"
      justifySelf="end"
      flexDir="column"
      gap="10px"
      w="100%"
      marginTop="auto"
    >
      <Flex
        w="100%"
        align="center"
        justify="space-between"
        py="12px"
        px="4px"
        borderTop="1px solid"
        borderColor={{ base: 'rgba(0,0,0,0.06)', _dark: 'rgba(255,255,255,0.06)' }}
      >
        <Flex align="center" gap="8px">
          <Text
            fontSize="13px"
            fontWeight="500"
            color={{ base: 'rgba(0,0,0,0.4)', _dark: 'rgba(255,255,255,0.4)' }}
          >
            Total
          </Text>
          {discountInfo && (
            <Flex
              align="center"
              gap="5px"
              pl="9px"
              pr={discountMinValueHint ? '5px' : '9px'}
              py="3px"
              borderRadius="full"
              bg={discountInfo.color}
              boxShadow="0 3px 10px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.25)"
              title={discountInfo.label}
            >
              <Text fontSize="11px" fontWeight="800" color="white">
                {discountInfo.badge}
              </Text>
              {discountMinValueHint && (
                <Tooltip content={discountMinValueHint} showArrow>
                  <Flex
                    as="span"
                    w="15px"
                    h="15px"
                    borderRadius="full"
                    bg="rgba(255,255,255,0.25)"
                    align="center"
                    justify="center"
                    cursor="help"
                    transition="all 0.18s ease"
                    _hover={{ bg: 'rgba(255,255,255,0.4)', transform: 'scale(1.15)' }}
                  >
                    <Icon w="8px" h="8px" color="white">
                      <FaInfo />
                    </Icon>
                  </Flex>
                </Tooltip>
              )}
            </Flex>
          )}
        </Flex>
        <Flex flexDir="column" align="flex-end" gap="2px">
          {discountInfo?.hasPriceReduction && (
            <Text
              fontSize="14px"
              fontWeight="500"
              color={{ base: 'rgba(0,0,0,0.35)', _dark: 'rgba(255,255,255,0.3)' }}
              letterSpacing="-0.3px"
              textDecoration="line-through"
            >
              {new Intl.NumberFormat('pt-PT', {
                style: 'currency',
                currency: 'EUR',
                currencyDisplay: 'symbol',
                currencySign: 'accounting',
              }).format(priceProductTotal)}
            </Text>
          )}
          <Text
            fontSize="28px"
            fontWeight="800"
            color={discountInfo?.hasPriceReduction ? discountInfo.color : { base: '#0A1B2B', _dark: '#E2EFF5' }}
            letterSpacing="-0.5px"
          >
            {new Intl.NumberFormat('pt-PT', {
              style: 'currency',
              currency: 'EUR',
              currencyDisplay: 'symbol',
              currencySign: 'accounting',
            }).format((() => {
              if (!discountInfo) return priceProductTotal;

              // Preço-alvo define um valor absoluto por unidade — ao
              // contrário de percent/fixed, não é uma proporção do
              // preço base, por isso nunca se pode escalar por rácio.
              // basePrice é o mínimo entre TODAS as variantes (pode
              // vir de uma cor/tamanho diferente do selecionado); se
              // for muito baixo, o rácio dispara para um valor
              // absurdo (ex: 50€ * (69/0,01) = 345 000€).
              if (discountInfo.rule.discount_type === 'target_price') {
                const quantity =
                  priceProduct > 0
                    ? priceProductTotal / priceProduct
                    : 1;
                return discountInfo.discountedPrice * quantity;
              }

              const base = getProductBasePrice(product) ?? priceProduct;
              const ratio = base > 0 ? discountInfo.discountedPrice / base : 1;
              return priceProductTotal * ratio;
            })())}
          </Text>
          {lowestPrice30d && (
            <Text
              fontSize="10.5px"
              color={{ base: 'rgba(0,0,0,0.4)', _dark: 'rgba(255,255,255,0.35)' }}
            >
              Preço mais baixo dos últimos 30 dias:{' '}
              {new Intl.NumberFormat('pt-PT', {
                style: 'currency',
                currency: 'EUR',
                currencyDisplay: 'symbol',
                currencySign: 'accounting',
              }).format(lowestPrice30d)}
            </Text>
          )}
        </Flex>
      </Flex>
      {limitStock > 0 || product.is_pre_sale ? (
        <Flex
          as="form"
          onSubmit={handleSubmit(onSubmit)}
          flexDir="column"
          gap="10px"
        >
          <Input
            type="hidden"
            {...register('product')}
            value={product.id}
          />
          <Flex align="center" gap="10px">
            <Controller
              control={control}
              name="quantity"
              defaultValue={1}
              render={({ field }) => (
                <FormInputNumber
                  step={1}
                  defaultValue="1"
                  min={1}
                  max={limitStock > 0 ? limitStock : 10}
                  error={errors.quantity?.message}
                  width="120px"
                  onValueChange={({ value }) => {
                    field.onChange(Number(value));
                    setPriceProductTotal(
                      priceProduct * (value ? Number(value) : 1),
                    );
                  }}
                />
              )}
            />
            <Button
              bg="#00B0CA"
              type="submit"
              style={{ fontSize: 'clamp(11px, 3.4vw, 15px)' }}
              flex="1"
              minW="0"
              px="clamp(8px, 3vw, 16px)"
              color="#fff"
              fontWeight="700"
              borderRadius="full"
              h="44px"
              boxShadow="0 4px 20px rgba(0,176,202,0.35)"
              _hover={{ bg: '#009AB5', boxShadow: '0 6px 24px rgba(0,176,202,0.45)' }}
              transition="all 0.2s"
            >
              <Icon
                style={{ width: 'clamp(14px, 4vw, 18px)', height: 'clamp(14px, 4vw, 18px)' }}
                mr="clamp(3px, 1.5vw, 6px)"
                flexShrink={0}
              >
                <MdOutlineShoppingCart />
              </Icon>
              <Text as="span" fontSize="inherit" fontWeight="inherit" whiteSpace="nowrap">
                Adicionar ao carrinho
              </Text>
            </Button>
            <WishlistButton
              productId={product.id}
              productOptId={wishlistOptId}
              productOptLabel={wishlistOptLabel}
              size="md"
              pos="static"
            />
          </Flex>
          <Text
            fontSize="12px"
            color={{ base: 'rgba(0,0,0,0.38)', _dark: 'rgba(255,255,255,0.35)' }}
            textAlign="center"
          >
            {limitStock > 0
              ? product.show_stock
                ? `${limitStock} ${limitStock > 1 ? 'unidades disponíveis' : 'unidade disponível'}`
                : 'Em stock'
              : 'Disponível para encomenda antecipada'}
          </Text>
          {product.special_conditions === 'meta' &&
            isPublished(LEGAL_DOCUMENTS.meta) && (
              <Text fontSize="12px" textAlign="center">
                <a
                  href={LEGAL_DOCUMENTS.meta.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#00B0CA', textDecoration: 'underline' }}
                >
                  Condições específicas de venda deste produto
                </a>
              </Text>
            )}
        </Flex>
      ) : (
        <Flex flexDir="column">
          <Flex
            align="center"
            justify="center"
            py="14px"
            borderRadius="12px"
            bg={{ base: 'rgba(220,53,69,0.06)', _dark: 'rgba(220,53,69,0.1)' }}
            border="1px solid rgba(220,53,69,0.15)"
          >
            <Text
              fontSize="14px"
              fontWeight="600"
              color="rgba(220,53,69,0.85)"
              textAlign="center"
            >
              Produto esgotado
            </Text>
          </Flex>
          <WaitlistForm productId={product.id} productOptId={waitlistOptId} />
        </Flex>
      )}
    </Flex>
  );

  // Só faz sentido falar de entrega quando dá para comprar.
  const canBuy = limitStock > 0 || product.is_pre_sale;

  if (product.product_type === 'CONTACT_LENSES') {
    // Mesmo painel único de sempre (fixo no fundo no mobile, sticky no
    // desktop) — só o título sai de lá para fora, para ficar logo a seguir
    // à galeria de fotos. O botão "Configurar lentes"/"Fechar" e o
    // formulário continuam dentro do mesmo painel, como já estava.
    return (
      <>
        <Flex display={{ base: 'flex', md: 'none' }} flexDir="column" w="100%" px="4px">
          <Text
            as="h1"
            fontSize="18px"
            fontWeight="700"
            color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}
            lineHeight="1.3"
          >
            {product.name}
          </Text>
          {brandModelBadges}
          {canBuy && (
            <Flex mt="12px" flexDir="column">
              <DeliveryOptions
                variant="compact"
                productTotal={priceProductTotal}
              />
            </Flex>
          )}
        </Flex>

        <Flex
          pos={{ base: 'fixed', md: 'sticky' }}
          data-bottom-bar
          top={{ base: 'unset', md: '90px' }}
          bottom={{ base: 0, md: 'unset' }}
          p={{ base: '16px', md: '24px' }}
          w="100%"
          maxH={{ base: 'none', md: 'none' }}
          overflowY={{ base: 'visible', md: 'auto' }}
          scrollbarWidth="none"
          css={{ '&::-webkit-scrollbar': { display: 'none' } }}
          h="fit-content"
          flexDir="column"
          bg={{ base: '#FFFFFF', _dark: '#0A1B2B' }}
          borderRadius={{ base: '20px 20px 0 0', md: '20px' }}
          boxShadow={{ base: '0 -6px 40px rgba(0,0,0,0.14)', md: '0 4px 24px rgba(0,0,0,0.08)' }}
          zIndex={{ base: 1000, md: 'unset' }}
          border="1px solid"
          borderColor={{ base: 'rgba(0,0,0,0.1)', md: 'rgba(0,0,0,0.07)' }}
          gridColumn={{ lg: '2' }}
          gridRow={{ lg: '1 / span 2' }}
        >
          {/* Repete o título de cima, só para o desktop (os dois ficam no
              HTML, só um visível de cada vez) — "p" e não "h1", para não
              haver dois H1 na página. */}
          <Flex display={{ base: 'none', md: 'flex' }} flexDir="column" mb="10px">
            <Text
              as="p"
              fontSize="22px"
              fontWeight="700"
              color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}
              lineHeight="1.3"
            >
              {product.name}
            </Text>
            {brandModelBadges}
          </Flex>
          {selectorsContent}
          {addView && ctaContent}
          {/* Só no desktop — no mobile este painel é a barra fixa no fundo,
              e a versão compacta já está no bloco do título, acima. */}
          {canBuy && (
            <Flex display={{ base: 'none', md: 'flex' }} flexDir="column">
              <DeliveryOptions variant="full" productTotal={priceProductTotal} />
            </Flex>
          )}
        </Flex>
      </>
    );
  }

  return (
    <>
      {/* Mobile: título + seletores no fluxo normal da página, logo a
          seguir à galeria de fotos — fora do painel fixo. */}
      <Flex
        display={{ base: 'flex', lg: 'none' }}
        flexDir="column"
        gap="10px"
        w="100%"
        bg={{ base: '#FFFFFF', _dark: '#0A1B2B' }}
        borderRadius="20px"
        border="1px solid"
        borderColor={{ base: 'rgba(0,0,0,0.07)', _dark: 'rgba(255,255,255,0.06)' }}
        boxShadow="0 2px 12px rgba(0,0,0,0.04)"
        p="20px"
      >
        <Flex align="flex-start" justify="space-between" gap="12px">
          <Text
            as="h1"
            fontSize="18px"
            fontWeight="700"
            color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}
            lineHeight="1.3"
          >
            {product.name}
          </Text>
          {isRayBan && <RayBanSeal size={44} />}
        </Flex>
        {selectorsContent}
        {/* Versão compacta aqui, no fluxo da página — não na barra fixa do
            fundo, que ficava a tapar meio ecrã. */}
        {canBuy && (
          <DeliveryOptions variant="compact" productTotal={priceProductTotal} />
        )}
      </Flex>

      {/* Desktop: painel sticky com tudo junto, inalterado. O título repete
          o do bloco de mobile acima (ambos ficam no HTML, só um visível de
          cada vez) — "p" e não "h1" aqui, para não haver dois H1 na página. */}
      <Flex
        display={{ base: 'none', lg: 'flex' }}
        pos="sticky"
        top="90px"
        p="24px"
        w="100%"
        h="fit-content"
        flexDir="column"
        bg={{ base: '#FFFFFF', _dark: '#0A1B2B' }}
        borderRadius="20px"
        boxShadow="0 4px 24px rgba(0,0,0,0.08)"
        border="1px solid"
        borderColor="rgba(0,0,0,0.07)"
        gridColumn="2"
        gridRow="1 / span 2"
      >
        <Flex align="flex-start" justify="space-between" gap="16px">
          <Text
            as="p"
            fontSize="22px"
            fontWeight="700"
            color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}
            lineHeight="1.3"
          >
            {product.name}
          </Text>
          {isRayBan && <RayBanSeal size={56} />}
        </Flex>
        <Box w="100%" mt="10px">
          {selectorsContent}
          {addView && ctaContent}
          {addView && canBuy && (
            <DeliveryOptions variant="full" productTotal={priceProductTotal} />
          )}
        </Box>
      </Flex>

      {/* Mobile: barra fixa no fundo só com preço + adicionar ao carrinho.
          Lentes de contacto tem o seu próprio painel (ramo acima), com a
          barra embutida — nunca chega aqui. */}
      <Flex
        display={{ base: 'flex', lg: 'none' }}
        pos="fixed"
        data-bottom-bar
        bottom="0"
        left="0"
        right="0"
        p="16px"
        flexDir="column"
        bg={{ base: '#FFFFFF', _dark: '#0A1B2B' }}
        borderRadius="20px 20px 0 0"
        boxShadow="0 -6px 40px rgba(0,0,0,0.14)"
        zIndex={1000}
        border="1px solid"
        borderColor="rgba(0,0,0,0.1)"
      >
        {ctaContent}
      </Flex>

      {colorPreviewDialog}
    </>
  );
};

export default ProductOptions;
